import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Check, Copy, Mail, Printer, ArrowLeft, Send } from 'lucide-react';
import Seo from '../components/Seo';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ETRANSFER_EMAIL = 'vialratpayments@gmail.com';

const STATUS_META = {
  AWAITING_PAYMENT:                   { label: 'AWAITING PAYMENT',                      tone: 'text-amber-400' },
  PAYMENT_SENT_AWAITING_VERIFICATION: { label: 'PAYMENT SENT — AWAITING VERIFICATION',  tone: 'text-sky-400' },
  PAID:                               { label: 'PAID',                                  tone: 'text-emerald-400' },
  PROCESSING:                         { label: 'PROCESSING',                            tone: 'text-emerald-400' },
  SHIPPED:                            { label: 'SHIPPED',                               tone: 'text-[#ff2fa1]' },
  CANCELLED:                          { label: 'CANCELLED',                             tone: 'text-neutral-400' },
};

/**
 * Normalise a server order shape OR a localStorage-cached order shape to the
 * single shape the page renders from. Snake-case fields (server) take precedence,
 * then camelCase fallbacks (local cache).
 */
const normaliseOrder = (raw) => {
  if (!raw) return null;
  const customer = raw.customer || {};
  const items = (raw.items || []).map(it => ({
    slug: it.slug,
    name: it.name || it.slug,
    dose: it.dose || it.variant_label || it.variantLabel || '',
    variantLabel: it.variant_label || it.variantLabel || null,
    preorder: !!it.preorder,
    qty: it.qty || 0,
    price: it.price || 0,
    priceDisplay: it.price_display || it.priceDisplay || '',
  }));
  return {
    ref: raw.ref,
    createdAt: raw.created_at || raw.createdAt,
    status: raw.status || 'AWAITING_PAYMENT',
    method: raw.method || 'INTERAC_ETRANSFER',
    fulfillment: raw.fulfillment || 'SHIP',
    customer,
    items,
    subtotal: raw.subtotal || 0,
    discounts: raw.discounts || [],
    discount_total: raw.discount_total ?? raw.discountTotal ?? 0,
    shipping: raw.shipping || 0,
    total: raw.total || 0,
  };
};

const loadFromLocal = (ref) => {
  try {
    const raw = localStorage.getItem('vrr_orders');
    const list = raw ? JSON.parse(raw) : [];
    return list.find(o => o.ref === ref) || null;
  } catch (e) { return null; }
};

const updateLocalStatus = (ref, status) => {
  try {
    const raw = localStorage.getItem('vrr_orders');
    const list = raw ? JSON.parse(raw) : [];
    const next = list.map(o => o.ref === ref ? { ...o, status } : o);
    localStorage.setItem('vrr_orders', JSON.stringify(next));
  } catch (e) { /* ignore */ }
};

const OrderConfirmation = () => {
  const { ref } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingPayment, setSendingPayment] = useState(false);

  // Load order: fetch from backend first (so the link is shareable across devices),
  // fallback to localStorage if the backend is unreachable.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`${API}/api/orders/${ref}`);
        if (res.ok) {
          const data = await res.json();
          if (!cancelled) setOrder(normaliseOrder(data));
          return;
        }
      } catch (e) { /* fallback below */ }
      if (!cancelled) {
        const local = loadFromLocal(ref);
        setOrder(local ? normaliseOrder(local) : null);
      }
    })().finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [ref]);

  const onPaymentSent = async () => {
    if (!order || sendingPayment) return;
    setSendingPayment(true);
    try {
      const res = await fetch(`${API}/api/orders/${order.ref}/payment-sent`, { method: 'POST' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Couldn't update your order. Please try again.");
        return;
      }
      const data = await res.json();
      setOrder(normaliseOrder(data));
      updateLocalStatus(order.ref, data.status || 'PAYMENT_SENT_AWAITING_VERIFICATION');
      toast.success('Thanks — we\'ve been notified.');
    } catch (err) {
      toast.error('Network error — please try again.');
    } finally {
      setSendingPayment(false);
    }
  };

  if (loading) {
    return (
      <main className="max-w-[900px] mx-auto px-6 md:px-10 pt-20 pb-24 text-center">
        <Seo title="Order | Vial Rat Research Co." description="Order confirmation." canonicalPath="/order" noindex />
        <p className="text-neutral-400 font-mono-label text-[11px]">LOADING ORDER…</p>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="max-w-[900px] mx-auto px-6 md:px-10 pt-20 pb-24 text-center">
        <Seo title="Order | Vial Rat Research Co." description="Order confirmation." canonicalPath="/order" noindex />
        <h1 className="font-serif-display text-white text-4xl mb-4">Order not found</h1>
        <p className="text-neutral-400 mb-8">We couldn't find that order. Please check your link.</p>
        <Link to="/shop" className="btn-pink inline-block px-8 py-4 rounded font-mono-label text-[11px]">BROWSE CATALOG</Link>
      </main>
    );
  }

  const statusMeta = STATUS_META[order.status] || STATUS_META.AWAITING_PAYMENT;
  const paymentReported = order.status !== 'AWAITING_PAYMENT';

  return (
    <main className="max-w-[900px] mx-auto px-6 md:px-10 pt-10 pb-20">
      <Seo title={`Order ${order.ref} | Vial Rat Research Co.`} description="Order confirmation." canonicalPath="/order" noindex />
      <Link to="/shop" className="inline-flex items-center gap-2 font-mono-label text-[11px] text-[#ff2fa1] hover:text-white transition-colors mb-6">
        <ArrowLeft size={14} /> BACK TO SHOP
      </Link>

      <div className="flex items-center gap-4 mt-4">
        <div className="w-14 h-14 rounded-full bg-[#ff2fa1]/10 border border-[#ff2fa1]/40 flex items-center justify-center">
          <Check size={26} className="text-[#ff2fa1]" />
        </div>
        <div>
          <div className={`font-mono-label text-[11px] ${statusMeta.tone}`} data-testid="order-status-label">
            ORDER STATUS · {statusMeta.label}
          </div>
          <h1 className="font-serif-display text-white text-4xl md:text-5xl mt-1">
            {paymentReported ? 'Thanks — we\'re verifying your payment.' : 'Thanks — one more step.'}
          </h1>
        </div>
      </div>

      <p className="mt-6 text-neutral-300 leading-relaxed max-w-2xl">
        {paymentReported ? (
          <>Your order is on file and your payment report has been received. We'll verify the deposit in RBC and move your order to <span className="text-white font-semibold">Paid / Processing</span> as soon as it arrives.</>
        ) : (
          <>Your order is on file. To finish, send an <span className="text-white font-semibold">Interac e-Transfer</span> using the details below. Once we verify the deposit, your order moves to <span className="text-white font-semibold">Paid / Processing</span> and ships within 1-2 business days.</>
        )}
      </p>

      {/* Payment instructions card */}
      <section className="mt-10 p-6 md:p-8 border border-[#ff2fa1]/30 rounded-lg bg-[#ff2fa1]/[0.04]">
        <div className="flex items-center gap-3">
          <Mail size={20} className="text-[#ff2fa1]" />
          <div className="font-mono-label text-[11px] text-[#ff2fa1]">SEND YOUR e-TRANSFER TO</div>
        </div>

        {/* The email itself is tappable to copy */}
        <TappableEmail email={ETRANSFER_EMAIL} />

        {/* Prominent copy button */}
        <div className="mt-5">
          <CopyButton
            value={ETRANSFER_EMAIL}
            idleLabel="COPY E-TRANSFER EMAIL"
            copiedLabel="✓ COPIED!"
            helperText="Email copied — paste it into your banking app."
            testId="copy-etransfer-email-btn"
            large
          />
        </div>

        <div className="mt-8 grid md:grid-cols-2 gap-6">
          <InstructionCard
            step="1" title="Amount"
            valueLarge={`$${order.total.toFixed(2)} CAD`}
            copyValue={order.total.toFixed(2)}
            copyIdle="COPY AMOUNT"
            copyDone="✓ COPIED!"
            testId="copy-amount-btn"
          />
          <InstructionCard
            step="2" title="Order Number / Reference"
            valueLarge={order.ref}
            valueSub="Please include this VR order number in the message section of your e-Transfer so we can match your payment to your order."
            copyValue={order.ref}
            copyIdle="COPY ORDER NUMBER"
            copyDone="✓ COPIED!"
            testId="copy-order-number-btn"
          />
        </div>

        <div className="mt-8 grid md:grid-cols-3 gap-3">
          <BulletStep n="3" text="Include your name and VR order number in the e-Transfer message field." />
          <BulletStep n="4" text="Auto-deposit is enabled — no security question needed." />
          <BulletStep n="5" text={order.fulfillment === 'LOCAL_PICKUP' ? "We'll email you privately to arrange pickup once payment clears." : "You'll get a shipping notification once payment clears."} />
        </div>

        {/* I've sent my e-Transfer */}
        <div className="mt-8 pt-6 border-t border-[#ff2fa1]/20">
          {paymentReported ? (
            <div className="w-full py-4 rounded border border-sky-400/40 bg-sky-400/5 text-sky-300 font-mono-label text-[11px] text-center flex items-center justify-center gap-2" data-testid="payment-reported-banner">
              <Check size={14} /> PAYMENT REPORTED — AWAITING VERIFICATION
            </div>
          ) : (
            <button
              type="button"
              onClick={onPaymentSent}
              disabled={sendingPayment}
              data-testid="payment-sent-btn"
              className={`w-full py-4 rounded font-mono-label text-[11px] inline-flex items-center justify-center gap-2 transition-colors ${sendingPayment ? 'bg-white/10 text-neutral-500 cursor-not-allowed' : 'btn-pink'}`}
            >
              <Send size={14} /> {sendingPayment ? 'NOTIFYING THE STORE…' : "I'VE SENT MY E-TRANSFER"}
            </button>
          )}
          <p className="mt-3 text-[11px] text-neutral-500 leading-relaxed">
            Clicking this notifies the store that your e-Transfer has been sent. Your order will stay <span className="text-white">Payment Sent — Awaiting Verification</span> until the deposit is confirmed in RBC.
          </p>
        </div>
      </section>

      {/* Order details */}
      <section className="mt-10">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="font-mono-label text-[11px] text-[#ff2fa1]">ORDER REFERENCE</div>
            <h2 className="font-serif-display text-white text-2xl mt-1" data-testid="order-ref-value">{order.ref}</h2>
            <div className="font-mono-label text-[10px] text-neutral-500 mt-1">
              PLACED {order.createdAt ? new Date(order.createdAt).toLocaleString('en-CA') : '—'}
            </div>
          </div>
          <button onClick={() => window.print()} className="btn-outline px-4 py-2 rounded font-mono-label text-[10px] inline-flex items-center gap-2">
            <Printer size={12} /> PRINT
          </button>
        </div>

        <div className="border border-white/10 rounded-lg divide-y divide-white/10">
          {order.items.map((i, idx) => (
            <div key={`${i.slug}-${i.dose}-${idx}`} className="flex items-center justify-between p-4">
              <div>
                <div className="font-serif-display text-white text-lg">{i.name}</div>
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <div className="font-mono-label text-[10px] text-neutral-500">{i.dose || '—'} · QTY {i.qty}</div>
                  {i.preorder && (
                    <span className="font-mono-label text-[9px] px-1.5 py-0.5 border border-[#ff2fa1]/40 text-[#ff2fa1] rounded">PRE-ORDER</span>
                  )}
                </div>
              </div>
              <div className="text-white text-sm">${((i.price || 0) * i.qty).toFixed(2)}</div>
            </div>
          ))}
          <div className="p-4 flex items-center justify-between text-sm">
            <span className="text-neutral-400">Subtotal</span>
            <span className="text-neutral-200">${order.subtotal.toFixed(2)}</span>
          </div>
          {order.discounts && order.discounts.map((d, i) => (
            <div key={i} className="p-4 flex items-start justify-between text-sm">
              <span className="text-[#ff2fa1] pr-3 leading-snug">{d.description}</span>
              <span className="text-[#ff2fa1] whitespace-nowrap">− ${(d.amount || 0).toFixed(2)}</span>
            </div>
          ))}
          <div className="p-4 flex items-center justify-between text-sm">
            {order.fulfillment === 'LOCAL_PICKUP' ? (
              <>
                <span className="text-neutral-400">Local Pickup — FREE</span>
                <span className="text-neutral-200">${order.shipping.toFixed(2)}</span>
              </>
            ) : (
              <>
                <span className="text-neutral-400">Shipping (Canada, tracked)</span>
                <span className="text-neutral-200">${order.shipping.toFixed(2)}</span>
              </>
            )}
          </div>
          <div className="p-4 flex items-center justify-between">
            <span className="font-mono-label text-[11px] text-neutral-400">TOTAL DUE</span>
            <span className="font-serif-display text-2xl text-white">${order.total.toFixed(2)}</span>
          </div>
        </div>
      </section>

      {/* Shipping / Pickup address */}
      <section className="mt-10 grid md:grid-cols-2 gap-6">
        <div className="p-5 border border-white/10 rounded-lg">
          {order.fulfillment === 'LOCAL_PICKUP' ? (
            <>
              <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">LOCAL PICKUP</div>
              <div className="text-white">{order.customer.firstName} {order.customer.lastName}</div>
              <div className="text-neutral-500 text-sm mt-2">{order.customer.email}</div>
              <div className="text-neutral-400 text-sm mt-3">Pickup details will be arranged privately by email after your order is confirmed.</div>
            </>
          ) : (
            <>
              <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">SHIPPING TO</div>
              <div className="text-white">{order.customer.firstName} {order.customer.lastName}</div>
              <div className="text-neutral-300 text-sm mt-1">{order.customer.address}</div>
              <div className="text-neutral-300 text-sm">{order.customer.city}, {order.customer.province} {order.customer.postal}</div>
              <div className="text-neutral-500 text-sm mt-2">{order.customer.email}</div>
            </>
          )}
          {order.customer.phone && <div className="text-neutral-500 text-sm">{order.customer.phone}</div>}
        </div>
        <div className="p-5 border border-white/10 rounded-lg">
          <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-3">PAYMENT METHOD</div>
          <div className="text-white">Interac e-Transfer</div>
          <div className="text-neutral-400 text-sm mt-1">Send to <span className="text-white">{ETRANSFER_EMAIL}</span></div>
          <div className={`font-mono-label text-[10px] mt-4 ${statusMeta.tone}`}>STATUS · {statusMeta.label}</div>
        </div>
      </section>

      <div className="mt-10 p-5 border border-white/10 rounded bg-white/[0.02]">
        <div className="font-mono-label text-[11px] text-[#ff2fa1] mb-2">FOR RESEARCH USE ONLY</div>
        <p className="text-sm text-neutral-400">Products are intended for laboratory and research purposes only. Not for human or veterinary use, food, or household application.</p>
      </div>
    </main>
  );
};

// ---- Reusable copy UI ----

const useCopy = () => {
  const [copied, setCopied] = useState(false);
  const copy = (value, msg) => {
    const done = () => {
      setCopied(true);
      if (msg) toast.success(msg);
      setTimeout(() => setCopied(false), 2000);
    };
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(value).then(done).catch(() => {
          // Fallback for older browsers / non-secure contexts.
          const ta = document.createElement('textarea');
          ta.value = value; document.body.appendChild(ta); ta.select();
          try { document.execCommand('copy'); done(); } catch (_) { /* ignore */ }
          document.body.removeChild(ta);
        });
      } else {
        const ta = document.createElement('textarea');
        ta.value = value; document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); done(); } catch (_) { /* ignore */ }
        document.body.removeChild(ta);
      }
    } catch (_) { /* ignore */ }
  };
  return { copied, copy };
};

const CopyButton = ({ value, idleLabel, copiedLabel, helperText, testId, large }) => {
  const { copied, copy } = useCopy();
  return (
    <div>
      <button
        type="button"
        onClick={() => copy(value, helperText)}
        data-testid={testId}
        className={`w-full ${large ? 'py-4 text-[12px]' : 'py-3 text-[11px]'} rounded font-mono-label inline-flex items-center justify-center gap-2 transition-colors ${copied ? 'bg-emerald-500 text-black' : 'btn-pink'}`}
      >
        {copied ? <Check size={14} /> : <Copy size={14} />}
        {copied ? copiedLabel : idleLabel}
      </button>
      {copied && helperText && (
        <p className="mt-2 text-[11px] text-emerald-400 text-center">{helperText}</p>
      )}
    </div>
  );
};

const TappableEmail = ({ email }) => {
  const { copied, copy } = useCopy();
  return (
    <button
      type="button"
      onClick={() => copy(email, 'Email copied — paste it into your banking app.')}
      data-testid="tappable-etransfer-email"
      className="mt-4 flex items-center gap-3 flex-wrap text-left group focus:outline-none"
      aria-label="Copy e-Transfer email"
    >
      <span className={`font-serif-display text-3xl md:text-4xl break-all transition-colors ${copied ? 'text-emerald-400' : 'text-white group-hover:text-[#ff2fa1]'}`}>
        {email}
      </span>
      <span className={`font-mono-label text-[10px] px-2 py-1 border rounded inline-flex items-center gap-1 transition-colors ${copied ? 'border-emerald-500/50 text-emerald-400' : 'border-[#ff2fa1]/40 text-[#ff2fa1]'}`}>
        {copied ? <><Check size={10} /> COPIED</> : <><Copy size={10} /> TAP TO COPY</>}
      </span>
    </button>
  );
};

const InstructionCard = ({ step, title, valueLarge, valueSub, copyValue, copyIdle, copyDone, testId }) => {
  const { copied, copy } = useCopy();
  return (
    <div className="p-5 rounded-lg border border-white/10 bg-black/30">
      <div className="flex items-center gap-3 mb-3">
        <span className="w-7 h-7 rounded-full bg-[#ff2fa1]/10 border border-[#ff2fa1]/40 text-[#ff2fa1] font-mono-label text-[11px] flex items-center justify-center">{step}</span>
        <span className="font-mono-label text-[11px] text-neutral-400">{title.toUpperCase()}</span>
      </div>
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <span className="font-serif-display text-white text-2xl break-all">{valueLarge}</span>
      </div>
      <button
        type="button"
        onClick={() => copy(copyValue)}
        data-testid={testId}
        className={`mt-4 w-full py-3 rounded font-mono-label text-[11px] inline-flex items-center justify-center gap-2 transition-colors ${copied ? 'bg-emerald-500 text-black' : 'btn-pink'}`}
      >
        {copied ? <Check size={12} /> : <Copy size={12} />}
        {copied ? copyDone : copyIdle}
      </button>
      {valueSub && <p className="text-xs text-neutral-500 mt-3 leading-relaxed">{valueSub}</p>}
    </div>
  );
};

const BulletStep = ({ n, text }) => (
  <div className="flex gap-3 items-start">
    <span className="shrink-0 w-6 h-6 rounded-full bg-white/5 border border-white/15 text-neutral-300 font-mono-label text-[10px] flex items-center justify-center">{n}</span>
    <span className="text-sm text-neutral-300 leading-relaxed">{text}</span>
  </div>
);

export default OrderConfirmation;
